/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import ReactDatetime from 'react-datetime';
import { toast } from 'react-toastify';
import { Badge, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import LabelColorPicker from './LabelColorPicker';
import { LABEL_COLORS } from './task-board.constants';
import { buildMemberNameMap, formatCommentTime, renderCommentWithMentions } from './task-board.comments';

import { selectAllOnFocus } from '../../../helpers/selectOnFocus';
type BoardMember = {
  userId: string;
  groupName?: string;
  user?: { id: string; firstName: string; lastName: string } | null;
};

const GROUP_LABEL: Record<string, string> = {
  Director: 'Director',
  EducationDirectorAdminAssistant: 'Ed Director / Admin Asst.',
  UpperManagement: 'Senior Manager',
  Manager: 'Manager',
};
type ChecklistItem = {
  id?: string;
  text: string;
  isDone: boolean;
  position: number;
  assignedUserId?: string | null;
  dueDate?: string | null;
};
type Checklist = { id?: string; title: string; items: ChecklistItem[] };
type CardAttachment = { id: string; fileName: string; fileMimetype: string; signedUrl: string };
type LabelItem = { text: string; color: string };

export type CardRecurrence = {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  endType: 'never' | 'on_date' | 'after_count';
  endDate?: string | null;
  maxOccurrences?: number | null;
  skipWeekends?: boolean;
  occurrencesGenerated: number;
  nextRunDate?: string | null;
  isActive: boolean;
};

export type CardRecurrencePayload = {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  endType: 'never' | 'on_date' | 'after_count';
  endDate?: string | null;
  maxOccurrences?: number | null;
  skipWeekends?: boolean;
};

type BoardCard = {
  id: string;
  listId?: string;
  title: string;
  description?: string | null;
  dueDate?: string | null;
  simpleLabels?: string[];
  labelsData?: LabelItem[] | null;
  members?: { userId: string; source?: string }[];
  checklists?: Checklist[];
  attachments?: CardAttachment[];
  recurrence?: CardRecurrence | null;
  isRecurrenceTemplate?: boolean;
  isRecurrenceInstance?: boolean;
};
export type CardComment = {
  id: string;
  text: string;
  createdAt: string;
  author?: { id: string; firstName: string; lastName: string } | null;
};

type TaskBoardCardModalProps = {
  isOpen: boolean;
  card: BoardCard | null;
  boardMembers: BoardMember[];
  saving: boolean;
  onClose: () => void;
  onSave: (payload: {
    title: string;
    description: string | null;
    dueDate: string | null;
    simpleLabels: string[];
    labelsData: LabelItem[] | null;
    memberUserIds: string[];
    checklists: Checklist[];
    recurrence: CardRecurrencePayload | null;
  }) => Promise<void>;
  onUploadAttachment?: (file: File) => Promise<CardAttachment | null>;
  onDeleteAttachment?: (attachmentId: string) => Promise<void>;
  onMarkCompleted?: () => Promise<void>;
  lists?: { id: string; name: string }[];
  onChangeList?: (listId: string) => Promise<boolean>;
  changingList?: boolean;
  comments?: CardComment[];
  loadingComments?: boolean;
  onAddComment?: (text: string) => Promise<CardComment | null>;
};

const TaskBoardCardModal = ({
  isOpen,
  card,
  boardMembers,
  saving,
  onClose,
  onSave,
  onUploadAttachment,
  onDeleteAttachment,
  onMarkCompleted,
  lists = [],
  onChangeList,
  changingList = false,
  comments = [],
  loadingComments = false,
  onAddComment,
}: TaskBoardCardModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [labelsData, setLabelsData] = useState<LabelItem[]>([]);
  const [newColorLabelText, setNewColorLabelText] = useState('');
  const [newColorLabelColor, setNewColorLabelColor] = useState(LABEL_COLORS[0].value);
  const [memberUserIds, setMemberUserIds] = useState<string[]>([]);
  const [autoMembers, setAutoMembers] = useState<{ userId: string }[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);

  const [recurrenceFrequency, setRecurrenceFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndType, setRecurrenceEndType] = useState<'never' | 'on_date' | 'after_count'>('never');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceMaxOccurrences, setRecurrenceMaxOccurrences] = useState(1);
  const [recurrenceSkipWeekends, setRecurrenceSkipWeekends] = useState(false);
  const [recurrenceEndDatePickerOpen, setRecurrenceEndDatePickerOpen] = useState(false);
  const [recurrenceEndDatePickerStyle, setRecurrenceEndDatePickerStyle] = useState<React.CSSProperties>({});
  const recurrenceEndDateInputRef = useRef<HTMLInputElement>(null);
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);

  const [attachments, setAttachments] = useState<CardAttachment[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionMapRef = useRef<Map<string, string>>(new Map());
  const [openItemMenuKey, setOpenItemMenuKey] = useState<string | null>(null);
  const itemMenuRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const commentsListRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const itemInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [pendingFocusKey, setPendingFocusKey] = useState<string | null>(null);

  const [titleError, setTitleError] = useState(false);
  const [listId, setListId] = useState('');
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);
  const addDropdownRef = useRef<HTMLDivElement>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerStyle, setDatePickerStyle] = useState<React.CSSProperties>({});
  const dateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!card) return;
    setTitle(card.title || '');
    setListId(card.listId || '');
    setDescription(card.description || '');
    const savedDue = card.dueDate ? moment.utc(String(card.dueDate)) : null;
    const savedHasTime = !!savedDue && (savedDue.hours() !== 0 || savedDue.minutes() !== 0);
    setDueDate(card.dueDate ? String(card.dueDate).slice(0, 10) : '');
    setDueTime(savedHasTime ? moment(String(card.dueDate)).format('HH:mm') : '');
    setLabelsData(card.labelsData || []);
    setMemberUserIds((card.members || []).filter((m) => m.source !== 'auto').map((m) => m.userId));
    setAutoMembers((card.members || []).filter((m) => m.source === 'auto'));
    setChecklists((card.checklists || []).map((checklist) => ({ ...checklist, items: [...(checklist.items || [])] })));
    setAttachments(card.attachments || []);
    setCommentText('');
    mentionMapRef.current = new Map();
    setNewColorLabelText('');
    setNewColorLabelColor(LABEL_COLORS[0].value);

    const recurrence = card.recurrence;
    setRecurrenceFrequency(recurrence?.frequency || 'weekly');
    setRecurrenceInterval(recurrence?.interval || 1);
    setRecurrenceEndType(recurrence?.endType || 'never');
    setRecurrenceEndDate(recurrence?.endDate ? String(recurrence.endDate).slice(0, 10) : '');
    setRecurrenceMaxOccurrences(recurrence?.maxOccurrences || 1);
    setRecurrenceSkipWeekends(!!recurrence?.skipWeekends);
    setRecurrenceEnabled(!!recurrence);

    const sections = new Set<string>();
    if ((card.labelsData || []).length > 0) sections.add('labels');
    if (card.dueDate) sections.add('dates');
    if ((card.members || []).length > 0) sections.add('members');
    if ((card.checklists || []).length > 0) sections.add('checklists');
    if ((card.attachments || []).length > 0) sections.add('attachments');
    if (recurrence) sections.add('recurrence');
    setOpenSections(sections);
  }, [card]);

  useEffect(() => {
    if (!pendingFocusKey) return;
    const el = itemInputRefs.current.get(pendingFocusKey);
    if (el) {
      el.focus();
      setPendingFocusKey(null);
    }
  }, [pendingFocusKey, checklists]);

  useEffect(() => {
    if (commentsListRef.current) {
      commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
    }
  }, [comments]);

  useEffect(() => {
    if (!addDropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) {
        setAddDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [addDropdownOpen]);

  useEffect(() => {
    if (!openItemMenuKey) return;
    const handle = (e: MouseEvent) => {
      if (itemMenuRef.current && !itemMenuRef.current.contains(e.target as Node)) {
        setOpenItemMenuKey(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openItemMenuKey]);

  const openSection = (section: string) => {
    setOpenSections((prev) => new Set([...prev, section]));
    setAddDropdownOpen(false);
  };

  const dueState = useMemo(() => {
    if (!dueDate) return null;
    const due = moment(dueTime ? dueDate + ' ' + dueTime : dueDate, dueTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD');
    if (!due.isValid()) return null;
    const today = moment().startOf('day');
    if (dueTime) {
      if (due.isBefore(moment())) return 'Overdue';
      if (due.isSame(today, 'day')) return 'Due today';
      return 'Upcoming';
    }
    if (due.isBefore(today)) return 'Overdue';
    if (due.isSame(today)) return 'Due today';
    return 'Upcoming';
  }, [dueDate, dueTime]);

  const addChecklist = () => {
    setChecklists((prev) => [...prev, { title: '', items: [] }]);
  };

  const addChecklistItem = (checklistIndex: number) => {
    const newItemIndex = checklists[checklistIndex].items.length;
    setPendingFocusKey(`${checklistIndex}-${newItemIndex}`);
    setChecklists((prev) =>
      prev.map((checklist, idx) =>
        idx === checklistIndex
          ? {
              ...checklist,
              items: [
                ...checklist.items,
                { text: '', isDone: false, position: checklist.items.length, assignedUserId: null, dueDate: null },
              ],
            }
          : checklist,
      ),
    );
  };

  const addColorLabel = () => {
    const text = newColorLabelText.trim();
    if (!text) return;
    setLabelsData((prev) => [...prev, { text, color: newColorLabelColor }]);
    setNewColorLabelText('');
  };

  const removeColorLabel = (index: number) => {
    setLabelsData((prev) => prev.filter((_, i) => i !== index));
  };

  const sortedMembers = useMemo(
    () =>
      [...(boardMembers || [])].sort((a, b) => {
        const aName = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.trim().toLowerCase();
        const bName = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.trim().toLowerCase();
        return aName.localeCompare(bName);
      }),
    [boardMembers],
  );

  const mentionNameMap = useMemo(() => buildMemberNameMap(sortedMembers), [sortedMembers]);

  const memberById = useMemo(() => {
    const map = new Map<string, BoardMember>();
    for (const member of sortedMembers) {
      map.set(member.userId, member);
    }
    return map;
  }, [sortedMembers]);

  const autoMemberUserIds = useMemo(() => new Set((autoMembers || []).map((m) => String(m.userId))), [autoMembers]);

  const availableMembersToAdd = useMemo(
    () =>
      sortedMembers.filter((member) => !memberUserIds.includes(member.userId) && !autoMemberUserIds.has(member.userId)),
    [sortedMembers, memberUserIds, autoMemberUserIds],
  );

  const selectedMembers = useMemo(
    () => memberUserIds.map((userId) => memberById.get(userId)).filter(Boolean) as BoardMember[],
    [memberById, memberUserIds],
  );

  const submit = async () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    if (recurrenceEnabled && !dueDate) {
      openSection('recurrence');
      toast.error('A due date is required to repeat this card.');
      return;
    }
    await onSave({
      title: title.trim(),
      description: description.trim() || null,
      dueDate: dueDate ? (dueTime ? dueDate + ' ' + dueTime : dueDate) : null,
      simpleLabels: [],
      labelsData: labelsData.length > 0 ? labelsData : null,
      memberUserIds,
      checklists: checklists.map((checklist, checklistIndex) => ({
        ...checklist,
        title: (checklist.title || '').trim() || `Checklist ${checklistIndex + 1}`,
        items: checklist.items
          .map((item, itemIndex) => ({
            ...item,
            text: item.text.trim(),
            position: itemIndex,
          }))
          .filter((item) => item.text.length > 0),
      })),
      recurrence: recurrenceEnabled
        ? {
            frequency: recurrenceFrequency,
            interval: recurrenceInterval,
            endType: recurrenceEndType,
            endDate: recurrenceEndType === 'on_date' ? recurrenceEndDate || null : null,
            maxOccurrences: recurrenceEndType === 'after_count' ? recurrenceMaxOccurrences : null,
            skipWeekends: recurrenceFrequency === 'daily' && recurrenceSkipWeekends,
          }
        : null,
    });
  };

  const submitComment = async () => {
    let text = commentText.trim();
    if (!text || !onAddComment) return;
    const mentionEntries = Array.from(mentionMapRef.current.entries()).sort((a, b) => b[0].length - a[0].length);
    for (const [name, userId] of mentionEntries) {
      text = text.replace(new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'), `@[${userId}]`);
    }
    setSendingComment(true);
    try {
      await onAddComment(text);
      setCommentText('');
      mentionMapRef.current = new Map();
    } finally {
      setSendingComment(false);
    }
  };

  const mentionMembers = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return sortedMembers.filter((m) => {
      const name = `${m.user?.firstName || ''} ${m.user?.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [mentionQuery, sortedMembers]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCommentText(value);
    const cursor = (e.target as unknown as HTMLTextAreaElement).selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, cursor);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex >= 0) {
      const query = textBeforeCursor.slice(atIndex + 1);
      if (!query.includes('\n') && !query.startsWith('[')) {
        setMentionQuery(query.toLowerCase());
        setMentionIndex(0);
        return;
      }
    }
    setMentionQuery(null);
  };

  const insertMention = (member: BoardMember) => {
    const el = commentInputRef.current;
    const cursor = el?.selectionStart ?? commentText.length;
    const textBeforeCursor = commentText.slice(0, cursor);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const before = commentText.slice(0, atIndex);
    const after = commentText.slice(cursor);
    const fullName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
    const displayToken = `@${fullName}`;
    const newText = `${before}${displayToken} ${after}`;
    setCommentText(newText);
    mentionMapRef.current.set(fullName, member.userId);
    setMentionQuery(null);
    setTimeout(() => {
      if (el) {
        const pos = atIndex + displayToken.length + 1;
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionQuery !== null && mentionMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionMembers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionMembers.length) % mentionMembers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionMembers[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
      e.preventDefault();
      void submitComment();
    }
  };

  const isNewCard = !card?.id;

  const showLabels = openSections.has('labels') || labelsData.length > 0;
  const showDates = openSections.has('dates') || !!dueDate;
  const showMembers = openSections.has('members') || memberUserIds.length > 0 || autoMembers.length > 0;
  const showChecklists = openSections.has('checklists') || checklists.length > 0;
  const showAttachments = !isNewCard && (openSections.has('attachments') || attachments.length > 0);
  const showRecurrence = openSections.has('recurrence') || !!card?.recurrence;

  const showListSelect = !isNewCard && !!onChangeList && lists.length > 0;

  const changeList = async (nextListId: string) => {
    if (!onChangeList || nextListId === listId) return;
    const previousListId = listId;
    setListId(nextListId);
    const moved = await onChangeList(nextListId);
    if (!moved) setListId(previousListId);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="task-board__card-modal">
      <div className="modal-header task-board__card-modal-header-block">
        <div className="task-board__modal-header-content">
          <input
            className={`task-board__modal-title-input${titleError ? ' task-board__modal-title-input--error' : ''}`}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (e.target.value.trim()) setTitleError(false);
            }}
            placeholder="Card title..."
          />
          {titleError && <span className="task-board__modal-title-error">Title is required.</span>}
          {(card?.isRecurrenceTemplate || card?.isRecurrenceInstance) && (
            <span className="task-board__card-pill task-board__card-pill--info">
              <i className="fas fa-redo" />
              {card?.isRecurrenceTemplate ? 'Recurring card' : 'Generated from recurrence'}
            </span>
          )}
          {showListSelect && (
            <div className="task-board__modal-list-select">
              <span className="task-board__modal-list-select-label">in list</span>
              <select
                className="task-board__modal-list-select-input"
                value={listId}
                disabled={changingList}
                onChange={(e) => {
                  void changeList(e.target.value);
                }}
              >
                {lists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="task-board__modal-action-bar">
            <div ref={addDropdownRef} className="task-board__add-dropdown-wrapper">
              <button
                type="button"
                className="task-board__action-btn task-board__action-btn--primary"
                onClick={() => setAddDropdownOpen((v) => !v)}
              >
                <i className="fas fa-plus" />
                Add
              </button>
              {addDropdownOpen && (
                <div className="task-board__add-dropdown">
                  {!showLabels && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => openSection('labels')}
                    >
                      <i className="fas fa-tag" />
                      Labels
                    </button>
                  )}
                  {!showDates && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => openSection('dates')}
                    >
                      <i className="far fa-calendar-alt" />
                      Dates
                    </button>
                  )}
                  {!showChecklists && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => {
                        openSection('checklists');
                        addChecklist();
                      }}
                    >
                      <i className="fas fa-check-square" />
                      Checklist
                    </button>
                  )}
                  {!showMembers && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => openSection('members')}
                    >
                      <i className="fas fa-user-plus" />
                      Members
                    </button>
                  )}
                  {!isNewCard && onUploadAttachment && !showAttachments && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => {
                        openSection('attachments');
                        fileRef.current?.click();
                      }}
                    >
                      <i className="fas fa-paperclip" />
                      Attachment
                    </button>
                  )}
                  {!showRecurrence && (
                    <button
                      type="button"
                      className="task-board__add-dropdown-item"
                      onClick={() => openSection('recurrence')}
                    >
                      <i className="fas fa-redo" />
                      Recurrence
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className={`task-board__action-btn${showDates ? ' task-board__action-btn--active' : ''}`}
              onClick={() => openSection('dates')}
            >
              <i className="far fa-calendar-alt" />
              Dates
            </button>
            <button
              type="button"
              className={`task-board__action-btn${showRecurrence ? ' task-board__action-btn--active' : ''}`}
              onClick={() => openSection('recurrence')}
            >
              <i className="fas fa-redo" />
              Recurrence
            </button>
            <button
              type="button"
              className={`task-board__action-btn${showChecklists ? ' task-board__action-btn--active' : ''}`}
              onClick={() => {
                openSection('checklists');
                if (!checklists.length) addChecklist();
              }}
            >
              <i className="fas fa-check-square" />
              Checklist
            </button>
            <button
              type="button"
              className={`task-board__action-btn${showMembers ? ' task-board__action-btn--active' : ''}`}
              onClick={() => openSection('members')}
            >
              <i className="fas fa-user" />
              Members
            </button>
          </div>
        </div>
        <button type="button" className="close task-board__modal-close-btn" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <ModalBody className="task-board__card-modal-body">
        <div className="task-board__card-modal-left">
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormGroup>

          {showLabels && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="fas fa-tag mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Labels
                </Label>
              </div>
              {labelsData.length > 0 && (
                <div className="task-board__chips mb-2">
                  {labelsData.map((label, idx) => (
                    <span
                      key={idx}
                      className="task-board__chip task-board__chip--active"
                      style={{
                        backgroundColor: label.color || '#6b7280',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      {label.text}
                      <button
                        type="button"
                        onClick={() => removeColorLabel(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          padding: 0,
                          lineHeight: 1,
                          opacity: 0.8,
                        }}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="task-board__simple-label-input-row">
                <Input
                  bsSize="sm"
                  placeholder="Label text..."
                  value={newColorLabelText}
                  onChange={(e) => setNewColorLabelText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addColorLabel();
                    }
                  }}
                />
                <LabelColorPicker value={newColorLabelColor} onChange={setNewColorLabelColor} />
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  onClick={addColorLabel}
                  disabled={!newColorLabelText.trim()}
                >
                  Add
                </Button>
              </div>
            </div>
          )}

          {showDates && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="far fa-calendar-alt mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Due date
                </Label>
                {dueState && (
                  <Badge color={dueState === 'Overdue' ? 'danger' : dueState === 'Due today' ? 'warning' : 'success'}>
                    {dueState}
                  </Badge>
                )}
              </div>
              <div className="task-board__date-field-row">
                <input
                  ref={dateInputRef}
                  className="form-control"
                  style={{ cursor: 'pointer' }}
                  placeholder="Due date..."
                  value={dueDate ? moment(dueDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}
                  onClick={() => {
                    if (dateInputRef.current) {
                      const rect = dateInputRef.current.getBoundingClientRect();
                      setDatePickerStyle({
                        position: 'fixed',
                        top: rect.bottom + 4,
                        left: rect.left,
                        zIndex: 999999999,
                      });
                    }
                    setDatePickerOpen(true);
                  }}
                />
                {datePickerOpen &&
                  createPortal(
                    <>
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 999999999 }}
                        onClick={() => setDatePickerOpen(false)}
                      />
                      <div style={datePickerStyle}>
                        <ReactDatetime
                          input={false}
                          value={dueDate ? moment(dueDate, 'YYYY-MM-DD') : ''}
                          onChange={(newValue) => {
                            const m = moment.isMoment(newValue) ? newValue : moment(String(newValue), 'MM/DD/YYYY');
                            setDueDate(m.isValid() ? m.format('YYYY-MM-DD') : '');
                            setDatePickerOpen(false);
                          }}
                          timeFormat={false}
                          dateFormat="MM/DD/YYYY"
                          isValidDate={(current: moment.Moment) => current.isSameOrAfter(moment().startOf('day'))}
                        />
                      </div>
                    </>,
                    document.body,
                  )}
                {dueDate && (
                  <button
                    type="button"
                    className="task-board__date-clear-btn"
                    onClick={() => {
                      setDueDate('');
                      setDueTime('');
                    }}
                    title="Clear due date"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              {dueDate && (
                <div className="mt-2">
                  <input
                    type="time"
                    className="form-control"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    aria-label="Due time"
                  />
                  <small className="text-muted">Optional — the center local time</small>
                </div>
              )}
            </div>
          )}

          {showRecurrence && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="fas fa-redo mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Recurrence
                </Label>
                {card?.recurrence && (
                  <Badge color={card.recurrence.isActive ? 'success' : 'secondary'}>
                    {card.recurrence.isActive ? 'Active' : 'Stopped'}
                  </Badge>
                )}
              </div>

              {card?.recurrence && (
                <p className="small text-muted mb-2">
                  Repeats every {card.recurrence.interval} {card.recurrence.frequency}
                  {card.recurrence.interval > 1 ? 's' : ''}
                  {card.recurrence.skipWeekends ? ' (weekdays only)' : ''}.{' '}
                  {card.recurrence.endType === 'after_count' &&
                    `Ends after ${card.recurrence.maxOccurrences} occurrences ` +
                      `(${card.recurrence.occurrencesGenerated} so far).`}
                  {card.recurrence.endType === 'on_date' &&
                    card.recurrence.endDate &&
                    `Ends on ${moment(card.recurrence.endDate).format('MM/DD/YYYY')}.`}
                  {card.recurrence.endType === 'never' && 'Never ends.'}
                </p>
              )}

              <div
                className="task-board__recurrence-row"
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                }}
              >
                <span className="small text-muted flex-shrink-0">Every</span>
                <Input
                  type="number"
                  {...selectAllOnFocus}
                  bsSize="sm"
                  min={1}
                  style={{ width: '70px', flexShrink: 0 }}
                  value={recurrenceInterval}
                  onChange={(e) => setRecurrenceInterval(Math.max(1, Number(e.target.value) || 1))}
                />
                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: '140px', flexShrink: 0 }}
                  value={recurrenceFrequency}
                  onChange={(e) => setRecurrenceFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
                >
                  <option value="daily">day(s)</option>
                  <option value="weekly">week(s)</option>
                  <option value="monthly">month(s)</option>
                </Input>

                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: '150px', flexShrink: 0 }}
                  value={recurrenceEndType}
                  onChange={(e) => setRecurrenceEndType(e.target.value as 'never' | 'on_date' | 'after_count')}
                >
                  <option value="never">Never ends</option>
                  <option value="on_date">Ends on</option>
                  <option value="after_count">Ends after</option>
                </Input>

                {recurrenceEndType === 'on_date' && (
                  <div className="task-board__date-field-row" style={{ flexShrink: 0 }}>
                    <input
                      ref={recurrenceEndDateInputRef}
                      className="form-control form-control-sm"
                      style={{ cursor: 'pointer', width: '140px' }}
                      placeholder="End date..."
                      value={recurrenceEndDate ? moment(recurrenceEndDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}
                      onClick={() => {
                        if (recurrenceEndDateInputRef.current) {
                          const rect = recurrenceEndDateInputRef.current.getBoundingClientRect();
                          setRecurrenceEndDatePickerStyle({
                            position: 'fixed',
                            top: rect.bottom + 4,
                            left: rect.left,
                            zIndex: 999999999,
                          });
                        }
                        setRecurrenceEndDatePickerOpen(true);
                      }}
                    />
                    {recurrenceEndDatePickerOpen &&
                      createPortal(
                        <>
                          <div
                            style={{ position: 'fixed', inset: 0, zIndex: 999999999 }}
                            onClick={() => setRecurrenceEndDatePickerOpen(false)}
                          />
                          <div style={recurrenceEndDatePickerStyle}>
                            <ReactDatetime
                              input={false}
                              value={recurrenceEndDate ? moment(recurrenceEndDate, 'YYYY-MM-DD') : ''}
                              onChange={(newValue) => {
                                const m = moment.isMoment(newValue) ? newValue : moment(String(newValue), 'MM/DD/YYYY');
                                setRecurrenceEndDate(m.isValid() ? m.format('YYYY-MM-DD') : '');
                                setRecurrenceEndDatePickerOpen(false);
                              }}
                              timeFormat={false}
                              dateFormat="MM/DD/YYYY"
                              isValidDate={(current: moment.Moment) => current.isSameOrAfter(moment().startOf('day'))}
                            />
                          </div>
                        </>,
                        document.body,
                      )}
                  </div>
                )}

                {recurrenceEndType === 'after_count' && (
                  <>
                    <Input
                      type="number"
                      {...selectAllOnFocus}
                      bsSize="sm"
                      min={1}
                      style={{ width: '70px', flexShrink: 0 }}
                      value={recurrenceMaxOccurrences}
                      onChange={(e) => setRecurrenceMaxOccurrences(Math.max(1, Number(e.target.value) || 1))}
                    />
                    <span className="small text-muted flex-shrink-0">occurrences</span>
                  </>
                )}
              </div>

              {recurrenceFrequency === 'daily' && (
                <div className="d-flex align-items-center mt-2">
                  <Input
                    type="checkbox"
                    id="recurrenceSkipWeekends"
                    checked={recurrenceSkipWeekends}
                    disabled={saving}
                    onChange={(e) => setRecurrenceSkipWeekends(e.target.checked)}
                    style={{ position: 'static', margin: 0 }}
                  />
                  <Label for="recurrenceSkipWeekends" className="mb-0 ml-1 small">
                    Skip weekends
                  </Label>
                </div>
              )}

              {!dueDate && <p className="small text-danger mb-2">A due date is required to set a recurrence.</p>}

              <div className="d-flex align-items-center mt-1">
                <Input
                  type="checkbox"
                  id="recurrenceEnabled"
                  checked={recurrenceEnabled}
                  disabled={saving || (!dueDate && !recurrenceEnabled)}
                  onChange={(e) => setRecurrenceEnabled(e.target.checked)}
                  style={{ position: 'static', margin: 0 }}
                />
                <Label for="recurrenceEnabled" className="mb-0 ml-1 small">
                  Repeat this card
                </Label>
              </div>
              <p className="small text-muted mb-0 mt-1">
                {recurrenceEnabled
                  ? 'Saved with the card. Uncheck and save to stop repeating.'
                  : 'Check to repeat this card, then save.'}
              </p>
            </div>
          )}

          {showMembers && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="fas fa-user mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Members
                </Label>
              </div>
              {autoMembers.length > 0 && (
                <div className="task-board__members-subsection">
                  <small className="task-board__members-subsection-label text-muted">
                    <i className="fas fa-list mr-1" style={{ fontSize: '0.65rem' }} />
                    From list
                  </small>
                  <div className="task-board__members-list">
                    {autoMembers.map((member) => {
                      const boardMember = memberById.get(member.userId);
                      const fullName =
                        `${boardMember?.user?.firstName || ''} ${boardMember?.user?.lastName || ''}`.trim() ||
                        'Unknown user';
                      const initials =
                        `${boardMember?.user?.firstName?.[0] || ''}${
                          boardMember?.user?.lastName?.[0] || ''
                        }`.toUpperCase() || 'U';
                      return (
                        <div key={member.userId} className="task-board__member-card task-board__member-card--auto">
                          <span className="task-board__member-avatar task-board__member-avatar--auto">{initials}</span>
                          <span className="task-board__member-name">{fullName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="task-board__members-subsection">
                <small className="task-board__members-subsection-label text-muted">
                  <i className="fas fa-hand-pointer mr-1" style={{ fontSize: '0.65rem' }} />
                  Manual ({memberUserIds.length})
                </small>
                <Input
                  type="select"
                  value=""
                  onChange={(event) => {
                    const id = event.target.value;
                    if (!id) return;
                    setMemberUserIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
                  }}
                  disabled={!availableMembersToAdd.length}
                >
                  <option value="">{availableMembersToAdd.length ? 'Select a member...' : 'All members added'}</option>
                  {availableMembersToAdd.map((member) => {
                    const fullName =
                      `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || 'Unknown user';
                    const profileLabel = member.groupName ? GROUP_LABEL[member.groupName] ?? member.groupName : '';
                    return (
                      <option key={member.userId} value={member.userId}>
                        {fullName}
                        {profileLabel ? ` (${profileLabel})` : ''}
                      </option>
                    );
                  })}
                </Input>
                <div className="task-board__members-list">
                  {selectedMembers.map((member) => {
                    const fullName =
                      `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || 'Unknown user';
                    const initials =
                      `${member.user?.firstName?.[0] || ''}${member.user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
                    const profileLabel = member.groupName ? GROUP_LABEL[member.groupName] ?? member.groupName : '';
                    return (
                      <div key={member.userId} className="task-board__member-card task-board__member-card--active">
                        <span className="task-board__member-avatar">{initials}</span>
                        <span className="task-board__member-name">
                          {fullName}
                          {profileLabel && <small className="text-muted ml-1">({profileLabel})</small>}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          color="link"
                          className="task-board__member-remove"
                          onClick={() => setMemberUserIds((prev) => prev.filter((id) => id !== member.userId))}
                        >
                          X
                        </Button>
                      </div>
                    );
                  })}
                  {!selectedMembers.length && <div className="task-board__empty-state">No manual members added.</div>}
                </div>
              </div>
            </div>
          )}

          {showChecklists && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="fas fa-check-square mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Checklists
                </Label>
                <Button size="sm" color="secondary" outline onClick={addChecklist} disabled={saving}>
                  + Add checklist
                </Button>
              </div>
              {!checklists.length && (
                <div className="task-board__empty-state">No checklists yet. Add one to organize tasks.</div>
              )}
              {checklists.map((checklist, checklistIndex) => (
                <div key={checklist.id || `checklist-${checklistIndex}`} className="task-board__checklist">
                  <div className="task-board__checklist-header">
                    <Input
                      className="task-board__checklist-title"
                      placeholder="Checklist title..."
                      value={checklist.title}
                      onChange={(event) =>
                        setChecklists((prev) =>
                          prev.map((item, idx) =>
                            idx === checklistIndex ? { ...item, title: event.target.value } : item,
                          ),
                        )
                      }
                    />
                    <Badge color="light" className="task-board__checklist-progress">
                      {checklist.items.filter((item) => item.isDone).length}/{checklist.items.length}
                    </Badge>
                  </div>
                  {checklist.items.map((item, itemIndex) => {
                    const itemKey = `${checklistIndex}-${itemIndex}`;
                    const menuOpen = openItemMenuKey === itemKey;
                    const assignedMember = item.assignedUserId ? memberById.get(item.assignedUserId) : null;
                    const assignedName = assignedMember
                      ? `${assignedMember.user?.firstName || ''} ${assignedMember.user?.lastName || ''}`.trim()
                      : null;
                    return (
                      <div key={item.id || `item-${itemIndex}`} className="task-board__checklist-item">
                        <input
                          type="checkbox"
                          className="task-board__checkbox"
                          checked={item.isDone}
                          onChange={() =>
                            setChecklists((prev) =>
                              prev.map((checklistItem, idx) =>
                                idx === checklistIndex
                                  ? {
                                      ...checklistItem,
                                      items: checklistItem.items.map((listItem, listIndex) =>
                                        listIndex === itemIndex ? { ...listItem, isDone: !listItem.isDone } : listItem,
                                      ),
                                    }
                                  : checklistItem,
                              ),
                            )
                          }
                        />
                        <div className="task-board__checklist-item-body ml-2">
                          <Input
                            className="task-board__checklist-item-input"
                            placeholder="Item..."
                            innerRef={(el) => {
                              if (el) itemInputRefs.current.set(itemKey, el);
                              else itemInputRefs.current.delete(itemKey);
                            }}
                            value={item.text}
                            onChange={(event) =>
                              setChecklists((prev) =>
                                prev.map((checklistItem, idx) =>
                                  idx === checklistIndex
                                    ? {
                                        ...checklistItem,
                                        items: checklistItem.items.map((listItem, listIndex) =>
                                          listIndex === itemIndex
                                            ? { ...listItem, text: event.target.value }
                                            : listItem,
                                        ),
                                      }
                                    : checklistItem,
                                ),
                              )
                            }
                          />
                          {(assignedName || item.dueDate) && (
                            <div className="task-board__checklist-item-chips">
                              {assignedName && (
                                <span className="task-board__checklist-item-chip">
                                  <i className="fas fa-user" />
                                  {assignedName}
                                </span>
                              )}
                              {item.dueDate &&
                                (() => {
                                  const due = moment(item.dueDate, 'YYYY-MM-DD').startOf('day');
                                  const isOverdue =
                                    !item.isDone && due.isValid() && due.isBefore(moment().startOf('day'));
                                  const base = 'task-board__checklist-item-chip task-board__checklist-item-chip--date';
                                  const chipClass = isOverdue
                                    ? `${base} task-board__checklist-item-chip--date--overdue`
                                    : base;
                                  return (
                                    <span className={chipClass}>
                                      <i className="fas fa-calendar-alt" />
                                      {due.isValid() ? due.format('MM/DD/YYYY') : item.dueDate}
                                    </span>
                                  );
                                })()}
                            </div>
                          )}
                        </div>
                        <div className="task-board__checklist-item-actions">
                          <div className="task-board__checklist-item-menu-wrapper" ref={menuOpen ? itemMenuRef : null}>
                            <button
                              type="button"
                              className="task-board__checklist-item-menu-btn"
                              onClick={() => setOpenItemMenuKey(menuOpen ? null : itemKey)}
                              title="Options"
                            >
                              <i className="fas fa-ellipsis-v" />
                            </button>
                            {menuOpen && (
                              <div className="task-board__checklist-item-dropdown">
                                <div className="task-board__checklist-item-dropdown-label">Assignee</div>
                                <select
                                  className="task-board__checklist-item-select"
                                  value={item.assignedUserId || ''}
                                  onChange={(e) => {
                                    const val = e.target.value || null;
                                    setChecklists((prev) =>
                                      prev.map((checklistItem, idx) =>
                                        idx === checklistIndex
                                          ? {
                                              ...checklistItem,
                                              items: checklistItem.items.map((listItem, listIndex) =>
                                                listIndex === itemIndex
                                                  ? { ...listItem, assignedUserId: val }
                                                  : listItem,
                                              ),
                                            }
                                          : checklistItem,
                                      ),
                                    );
                                  }}
                                >
                                  <option value="">No assignee</option>
                                  {sortedMembers.map((m) => (
                                    <option key={m.userId} value={m.userId}>
                                      {`${m.user?.firstName || ''} ${m.user?.lastName || ''}`.trim()}
                                    </option>
                                  ))}
                                </select>
                                <div className="task-board__checklist-item-dropdown-label mt-2">Due date</div>
                                <ReactDatetime
                                  value={item.dueDate ? moment(item.dueDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}
                                  timeFormat={false}
                                  dateFormat="MM/DD/YYYY"
                                  inputProps={{
                                    placeholder: 'Pick a date',
                                    className: 'task-board__checklist-item-date',
                                    readOnly: true,
                                  }}
                                  onChange={(val) => {
                                    const dateStr =
                                      val && typeof val !== 'string'
                                        ? val.format('YYYY-MM-DD')
                                        : val
                                        ? moment(val as string, 'MM/DD/YYYY').format('YYYY-MM-DD')
                                        : null;
                                    setChecklists((prev) =>
                                      prev.map((checklistItem, idx) =>
                                        idx === checklistIndex
                                          ? {
                                              ...checklistItem,
                                              items: checklistItem.items.map((listItem, listIndex) =>
                                                listIndex === itemIndex ? { ...listItem, dueDate: dateStr } : listItem,
                                              ),
                                            }
                                          : checklistItem,
                                      ),
                                    );
                                  }}
                                />
                                {(item.assignedUserId || item.dueDate) && (
                                  <button
                                    type="button"
                                    className="task-board__checklist-item-clear"
                                    onClick={() =>
                                      setChecklists((prev) =>
                                        prev.map((checklistItem, idx) =>
                                          idx === checklistIndex
                                            ? {
                                                ...checklistItem,
                                                items: checklistItem.items.map((listItem, listIndex) =>
                                                  listIndex === itemIndex
                                                    ? { ...listItem, assignedUserId: null, dueDate: null }
                                                    : listItem,
                                                ),
                                              }
                                            : checklistItem,
                                        ),
                                      )
                                    }
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            color="link"
                            className="task-board__checklist-remove"
                            onClick={() =>
                              setChecklists((prev) =>
                                prev.map((checklistItem, idx) =>
                                  idx === checklistIndex
                                    ? {
                                        ...checklistItem,
                                        items: checklistItem.items.filter((_, listIndex) => listIndex !== itemIndex),
                                      }
                                    : checklistItem,
                                ),
                              )
                            }
                            disabled={saving}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {!checklist.items.length && (
                    <div className="task-board__empty-state task-board__empty-state--compact">No items yet.</div>
                  )}
                  <div className="task-board__checklist-footer">
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      onClick={() => addChecklistItem(checklistIndex)}
                      disabled={saving}
                    >
                      + Add item
                    </Button>
                    <Button
                      size="sm"
                      color="link"
                      className="task-board__checklist-remove-list"
                      onClick={() => setChecklists((prev) => prev.filter((_, idx) => idx !== checklistIndex))}
                      disabled={saving}
                    >
                      Remove checklist
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAttachments && onUploadAttachment && (
            <div className="task-board__modal-section">
              <div className="task-board__section-header">
                <Label className="mb-0">
                  <i className="fas fa-paperclip mr-1 text-muted" style={{ fontSize: '0.75rem' }} />
                  Attachments
                </Label>
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  type="button"
                  disabled={saving || uploadingFile}
                  onClick={() => fileRef.current?.click()}
                >
                  <i className="fas fa-paperclip mr-1" />
                  {uploadingFile ? 'Uploading...' : 'Attach file'}
                </Button>
              </div>
              <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                PNG, JPG, PDF, XLSX &mdash; max 5 MB
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".png,.jpg,.jpeg,.pdf,.xlsx"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !onUploadAttachment) return;
                  e.target.value = '';
                  setUploadingFile(true);
                  const result = await onUploadAttachment(file);
                  if (result) setAttachments((prev) => [...prev, result]);
                  setUploadingFile(false);
                }}
              />
              {!attachments.length && <div className="task-board__empty-state">No attachments yet.</div>}
              {attachments.map((att) => (
                <div key={att.id} className="d-flex align-items-center justify-content-between py-1">
                  <a
                    href={att.signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-truncate mr-2"
                    style={{ maxWidth: '80%' }}
                  >
                    <i className="fas fa-paperclip mr-1 text-muted" />
                    {att.fileName}
                  </a>
                  {onDeleteAttachment && (
                    <Button
                      type="button"
                      size="sm"
                      color="link"
                      className="text-danger p-0"
                      disabled={saving}
                      onClick={async () => {
                        await onDeleteAttachment(att.id);
                        setAttachments((prev) => prev.filter((a) => a.id !== att.id));
                      }}
                    >
                      <i className="far fa-trash-alt" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="task-board__card-modal-right">
          <div className="task-board__comments">
            <div className="task-board__comments-title">
              <i className="far fa-comment-alt mr-1" />
              Comments
              {comments.length > 0 && (
                <span className="ml-1 text-muted" style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                  ({comments.length})
                </span>
              )}
            </div>

            {isNewCard ? (
              <div className="task-board__empty-state">Save the card first to add comments.</div>
            ) : (
              <>
                <div className="task-board__comments-list" ref={commentsListRef}>
                  {loadingComments ? (
                    <div className="text-center text-muted py-3" style={{ fontSize: '0.84rem' }}>
                      Loading comments...
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="task-board__empty-state">No comments yet. Be the first to comment!</div>
                  ) : (
                    comments.map((comment) => {
                      const firstName = comment.author?.firstName || '';
                      const lastName = comment.author?.lastName || '';
                      const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
                      const authorName = `${firstName} ${lastName}`.trim() || 'Unknown';
                      return (
                        <div key={comment.id} className="task-board__comment">
                          <span className="task-board__comment-avatar">{initials}</span>
                          <div className="task-board__comment-body">
                            <div className="task-board__comment-meta">
                              <span className="task-board__comment-author">{authorName}</span>
                              <span className="task-board__comment-time">{formatCommentTime(comment.createdAt)}</span>
                            </div>
                            <div className="task-board__comment-bubble">
                              {renderCommentWithMentions(comment.text, mentionNameMap)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {onAddComment && (
                  <div className="task-board__comment-input-row" style={{ position: 'relative' }}>
                    {mentionQuery !== null && mentionMembers.length > 0 && (
                      <div className="task-board__mention-dropdown">
                        {mentionMembers.map((member, idx) => {
                          const fullName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
                          const initials =
                            `${member.user?.firstName?.[0] || ''}${member.user?.lastName?.[0] || ''}`.toUpperCase() ||
                            '?';
                          return (
                            <button
                              key={member.userId}
                              type="button"
                              className={`task-board__mention-item${
                                idx === mentionIndex ? ' task-board__mention-item--active' : ''
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                insertMention(member);
                              }}
                              onMouseEnter={() => setMentionIndex(idx)}
                            >
                              <span className="task-board__mention-avatar">{initials}</span>
                              {fullName}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <Input
                      type="textarea"
                      rows={2}
                      placeholder="Write a comment... (@ to mention)"
                      value={commentText}
                      innerRef={commentInputRef as any}
                      onChange={handleCommentChange}
                      onKeyDown={handleCommentKeyDown}
                      disabled={sendingComment}
                    />
                    <Button
                      color="primary"
                      size="sm"
                      className="mt-2"
                      style={{ width: '100%' }}
                      disabled={!commentText.trim() || sendingComment}
                      onClick={() => void submitComment()}
                    >
                      {sendingComment ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex flex-wrap align-items-center">
        <div className="mr-auto">
          {onMarkCompleted && (
            <Button
              type="button"
              color="success"
              outline
              onClick={() => void onMarkCompleted()}
              disabled={saving || !card}
            >
              Mark as completed
            </Button>
          )}
        </div>
        <Button color="secondary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={submit} disabled={saving || !card}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TaskBoardCardModal;
